import { useEffect, useMemo, useRef, useState } from 'react';
import { QueryLoadingState } from '../../components/QueryStates';
import { useParams, Link } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody,
  Col, Form, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useMe } from '../../me/MeProvider';
import { getMtffPublicUrl, isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useCreateThemeVersion,
  useMintPreviewToken,
  useMtffInstance,
  useMtffThemeVersion,
  useMtffThemes,
  usePublishThemeVersion,
} from './useMtff';

/**
 * MTFF Theme-Editor pro Instance.
 *
 * Spec:
 *  - 02_applications/07_MTFF/theming.md §11 (Preview-Token-Workflow)
 *  - MTFF/docs/ORBIT-INTEGRATION.md §3.2 (Theme-Endpoints)
 *  - src/lib/theme/tokens.ts (Token-Schema)
 *
 * Drei Bereiche:
 *  1. Token-Form (9 Felder, Safe-Set-validiert)
 *  2. Versions-Liste (latest + Draft-History) mit Publish-Button
 *  3. Preview-iframe mit Mint + TTL-Countdown
 *
 * Editor-Workflow:
 *   Token ändern → „Draft speichern" → neue Version mit isPublished=false
 *   → „Vorschau" mintet Preview-Token gegen die Draft-Version
 *   → iframe zeigt MTFF-Customer-Origin mit Preview-Cookie
 *   → „Veröffentlichen" toggelt Publish, CDN propagiert in ~30s
 */

// Token-Schema spiegelt src/lib/theme/tokens.ts (MTFF v0.13). Reihenfolge
// + Label sind UX-Choice. Color-Tokens kriegen ein Color-Picker-Input.
const TOKEN_DEFS = [
  { key: 'color.brand',   label: 'Brand-Farbe',     kind: 'color' },
  { key: 'color.accent',  label: 'Akzent',          kind: 'color' },
  { key: 'color.surface', label: 'Surface',         kind: 'color' },
  { key: 'color.ink',     label: 'Ink (Text)',      kind: 'color' },
  { key: 'color.muted',   label: 'Muted Text',      kind: 'color' },
  { key: 'font.body',     label: 'Body-Font',       kind: 'text', placeholder: 'Inter' },
  { key: 'font.heading',  label: 'Heading-Font',    kind: 'text', placeholder: 'Inter' },
  { key: 'radius.card',   label: 'Card-Radius',     kind: 'text', placeholder: '12px' },
  { key: 'layout.contentMax', label: 'Content-Max', kind: 'text', placeholder: '1180px' },
];

// Safe-Set per Spec §4.1 — MTFF lehnt sonst mit 400 validation_failed ab.
const SAFE_SET = /^[a-zA-Z0-9#%(),.\- ]+$/;

export function MtffThemeEditorPage() {
  const { instanceId } = useParams();
  const me = useMe().me;
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const enabled = isMtffProxyEnabled();

  const instance = useMtffInstance(instanceId);
  const themes = useMtffThemes(instanceId);
  const currentVersion = instance.data?.themeVersion ?? null;
  const currentTheme = useMtffThemeVersion(instanceId, currentVersion);

  // Render-Gate: Editor wird erst gemountet wenn currentTheme.data steht.
  // Damit kann useState im Body die Initial-Tokens synchron aus props
  // lesen — kein setState-in-useEffect-Cascade.
  if (!enabled || !currentTheme.data?.tokens) {
    return (
      <div>
        <PageHeader
          title={`MTFF Theme · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        />
        {!enabled ? (
          <Alert color="warning" className="small">
            MTFF-Admin-Proxy nicht konfiguriert. Setze ENV-Variablen.
          </Alert>
        ) : (
          <QueryLoadingState label="Theme wird geladen…" />
        )}
      </div>
    );
  }

  return (
    <ThemeEditorBody
      key={`${instanceId}-${currentVersion}`}
      instanceId={instanceId}
      instance={instance}
      themes={themes}
      currentTheme={currentTheme}
      currentVersion={currentVersion}
      canWrite={canWrite}
      me={me}
    />
  );
}

function ThemeEditorBody({
  instanceId, instance, themes, currentTheme, currentVersion,
  canWrite, me,
}) {
  // Lazy-Init aus currentTheme.tokens. Da der Parent erst rendert wenn
  // Data steht, ist das beim ersten Render verfügbar — kein useEffect-Sync.
  const [tokens, setTokens] = useState(() => ({ ...currentTheme.data.tokens }));

  const create = useCreateThemeVersion();
  const publish = usePublishThemeVersion();
  const mint = useMintPreviewToken();
  const [draftVersion, setDraftVersion] = useState(null);
  const [preview, setPreview] = useState(null); // { token, enterUrl, expiresAt, auditId }

  const dirty = useMemo(() => {
    const orig = currentTheme.data?.tokens ?? {};
    return TOKEN_DEFS.some((d) => (tokens[d.key] ?? '') !== (orig[d.key] ?? ''));
  }, [tokens, currentTheme.data]);

  const validationError = useMemo(() => {
    for (const d of TOKEN_DEFS) {
      const v = tokens[d.key];
      if (v && !SAFE_SET.test(v)) {
        return `${d.label}: ungültiges Zeichen — erlaubt nur [a-zA-Z0-9#%(),.\\- ].`;
      }
    }
    return null;
  }, [tokens]);

  const onSaveDraft = async (e) => {
    e?.preventDefault();
    if (validationError) return;
    const res = await create.mutateAsync({ instanceId, tokens });
    if (res?.version != null) setDraftVersion(res.version);
  };

  const onPublishCurrent = async () => {
    if (draftVersion == null) return;
    await publish.mutateAsync({ instanceId, version: draftVersion });
    setDraftVersion(null);
    instance.refetch();
    themes.refetch();
  };

  const onMintPreview = async () => {
    const ver = draftVersion ?? currentVersion;
    if (!ver) return;
    const res = await mint.mutateAsync({
      instanceId,
      actor: `orbit-user-${me?.user?.id ?? 'unknown'}`,
      ttlSec: 15 * 60,
      themeVersion: ver,
    });
    if (res?.token) {
      setPreview({
        token: res.token,
        enterUrl: res.enterUrl,
        expiresAt: Date.now() + (res.expiresIn ?? 900) * 1000,
        auditId: res.auditId,
        version: ver,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title={`MTFF Theme · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle={
          currentVersion
            ? `Live: v${currentVersion} · ${instance.data?.domains?.[0]?.hostname ?? ''}`
            : 'Theme-Editor'
        }
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Row className="g-3">
        <Col lg="7">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="text-uppercase small text-muted mb-0">
                  Token-Editor (v{currentVersion ?? '?'})
                </h6>
                {dirty && (
                  <Badge color="warning" pill>ungespeicherte Änderungen</Badge>
                )}
              </div>

              <Form onSubmit={onSaveDraft}>
                {TOKEN_DEFS.map((d) => (
                  <FormGroup key={d.key} row className="mb-2 align-items-center">
                    <Label sm="4" className="small mb-0">
                      {d.label} <code className="text-muted">{d.key}</code>
                    </Label>
                    <Col sm="8">
                      <div className="d-flex gap-2 align-items-center">
                        {d.kind === 'color' && (
                          <Input
                            type="color"
                            value={isHexColor(tokens[d.key]) ? tokens[d.key] : '#000000'}
                            onChange={(e) => setTokens((t) => ({ ...t, [d.key]: e.target.value }))}
                            disabled={!canWrite}
                            style={{ width: 40, padding: 2 }}
                          />
                        )}
                        <Input
                          type="text"
                          bsSize="sm"
                          value={tokens[d.key] ?? ''}
                          onChange={(e) => setTokens((t) => ({ ...t, [d.key]: e.target.value }))}
                          placeholder={d.placeholder}
                          disabled={!canWrite}
                          pattern="[a-zA-Z0-9#%(),.\- ]+"
                        />
                      </div>
                    </Col>
                  </FormGroup>
                ))}

                {validationError && (
                  <Alert color="danger" className="small mb-2">{validationError}</Alert>
                )}

                <HasPermission perms={['integrations.write']}>
                  <ButtonGroup size="sm" className="mt-2">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={!canWrite || !dirty || !!validationError || create.isPending}
                    >
                      {create.isPending ? 'Speichere…' : '💾 Draft speichern'}
                    </Button>
                    <Button
                      color="success"
                      onClick={onPublishCurrent}
                      disabled={!canWrite || draftVersion == null || publish.isPending}
                      title={draftVersion != null
                        ? `v${draftVersion} veröffentlichen`
                        : 'Erst Draft speichern, dann Publish'}
                    >
                      {publish.isPending ? 'Publish…' : `✓ Veröffentlichen${draftVersion != null ? ` v${draftVersion}` : ''}`}
                    </Button>
                  </ButtonGroup>
                </HasPermission>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col lg="5">
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-uppercase small text-muted mb-2">Versionen</h6>
              <ul className="list-unstyled small mb-0">
                {(themes.data?.themes ?? []).map((t) => (
                  <li key={t.version} className="d-flex justify-content-between align-items-center mb-1">
                    <span>
                      <code>v{t.version}</code>
                      {' '}
                      {t.isPublished && <Badge color="success" pill className="ms-1">live</Badge>}
                    </span>
                    <span className="text-muted">
                      {t.createdAt && new Date(t.createdAt).toLocaleString('de-DE')}
                    </span>
                  </li>
                ))}
                {themes.isLoading && <li className="text-muted">Lade…</li>}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="text-uppercase small text-muted mb-0">Vorschau</h6>
                <HasPermission perms={['integrations.write']}>
                  <Button
                    color="info"
                    outline
                    size="sm"
                    onClick={onMintPreview}
                    disabled={!canWrite || mint.isPending}
                  >
                    {mint.isPending ? 'Mint…' : (preview ? '↻ Refresh-Token' : '👁 Vorschau starten')}
                  </Button>
                </HasPermission>
              </div>
              {preview ? (
                <PreviewPanel preview={preview} onExit={() => setPreview(null)} />
              ) : (
                <p className="small text-muted mb-0">
                  Startet einen MTFF-Preview-Token gegen v{draftVersion ?? currentVersion ?? '?'}.
                  Mintet ein signiertes Token (TTL 15 min) und rendert die
                  Customer-Origin in einem iframe mit Preview-Cookie.
                </p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

function PreviewPanel({ preview, onExit }) {
  const mtffHost = getMtffPublicUrl();
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.floor((preview.expiresAt - Date.now()) / 1000)),
  );
  const iframeRef = useRef(null);

  useEffect(() => {
    const handle = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.floor((preview.expiresAt - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(handle);
  }, [preview.expiresAt]);

  const exit = () => {
    // Pro Spec §11.6: Exit-Link feuert /api/preview-theme/exit im iframe
    if (iframeRef.current && mtffHost) {
      try {
        iframeRef.current.src = `${mtffHost}/api/preview-theme/exit?to=/`;
      } catch {
        /* cross-origin — Browser blockt direct property set nicht, aber sicherheitshalber */
      }
    }
    onExit();
  };

  const minutes = Math.floor(secondsLeft / 60);
  const sec = String(secondsLeft % 60).padStart(2, '0');
  const expiringSoon = secondsLeft < 60;

  if (!mtffHost) {
    return (
      <Alert color="warning" className="small mb-0">
        <code>MTFF_PUBLIC_URL</code> nicht gesetzt — Preview-iframe-Quelle
        unbekannt. ENV ergänzen, dann nochmal minten.
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 small">
        <span>
          v{preview.version}
          {preview.auditId && <span className="text-muted ms-2">audit#{preview.auditId}</span>}
        </span>
        <Badge color={expiringSoon ? 'danger' : 'info'} pill>
          {minutes}:{sec}
        </Badge>
      </div>
      <iframe
        ref={iframeRef}
        title="MTFF Theme Preview"
        src={`${mtffHost}${preview.enterUrl}`}
        sandbox="allow-same-origin allow-scripts allow-forms"
        style={{
          width: '100%',
          height: 480,
          border: '1px solid var(--bs-border-color)',
          borderRadius: 6,
        }}
      />
      <div className="d-flex justify-content-end mt-2">
        <Button color="secondary" outline size="sm" onClick={exit}>
          Vorschau beenden
        </Button>
      </div>
    </>
  );
}

function isHexColor(v) {
  return typeof v === 'string' && /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(v);
}
